import React, { useEffect, useState } from 'react';
import { Pencil, Plus, Search, Trash2, XCircle } from 'lucide-react';
import { servicesApi } from '../../api/servicesApi';
import Modal from '../../components/Modal';
import ServiceIcon, { ICON_MAP } from '../../components/ServiceIcon';
import { AdminLayout } from '../../components/layouts';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Select,
  TableShell,
  Textarea,
  tableClassName,
  tdClassName,
  thClassName,
} from '../../components/ui';
import { MIN_SERVICE_HOURS, formatHours } from '../../utils/duration';

const ICON_OPTIONS = Object.keys(ICON_MAP);

const emptyService = {
  slug: '',
  name: '',
  icon: 'handyman',
  description: '',
  status: 'Active',
  sortOrder: 0,
  checklist: [],
  hourlyRate: '',
  minBookingHours: 1,
};

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const STATUS_VARIANT = {
  Active: 'success',
  'Coming Soon': 'info',
  Inactive: 'warning',
  Suspended: 'danger',
};


const serviceRate = (service) => {
  const min = Number(service?.hourlyRateMin) || 0;
  const max = Number(service?.hourlyRateMax) || 0;
  return min || max || 0;
};

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(emptyService);
  const [newTask, setNewTask] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadServices = async () => {
    try {
      const response = await servicesApi.adminList();
      setServices(response.data || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load services.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadServices(); }, []);

  const filtered = services.filter((service) => {
    const query = search.toLowerCase();
    const matchSearch = service.name.toLowerCase().includes(query)
      || (service.slug || '').toLowerCase().includes(query);
    return matchSearch && (filterStatus === 'All' || service.status === filterStatus);
  });

  const resetTaskDraft = () => setNewTask('');

  const openCreate = () => {
    setEditingService(null);
    setFormData({ ...emptyService, checklist: [] });
    resetTaskDraft();
    setError('');
    setModalOpen(true);
  };

  const openEdit = (service) => {
    const checklist = Array.isArray(service.checklist) ? [...service.checklist] : [];

    setEditingService(service);
    setFormData({
      slug: service.slug || '',
      name: service.name || '',
      icon: service.icon || 'handyman',
      description: service.description || '',
      status: service.status || 'Active',
      sortOrder: service.sortOrder || 0,
      checklist,
      hourlyRate: serviceRate(service) || '',
      minBookingHours: service.minBookingHours ?? 1,
    });
    resetTaskDraft();
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingService(null);
    resetTaskDraft();
    setError('');
  };

  const addTask = () => {
    const task = newTask.trim();
    if (!task) return;
    if (formData.checklist.includes(task)) {
      setError('That sub service is already associated with this service.');
      return;
    }

    setFormData((current) => ({
      ...current,
      checklist: [...current.checklist, task],
    }));
    resetTaskDraft();
    setError('');
  };

  const removeTask = (index) => {
    setFormData({
      ...formData,
      checklist: formData.checklist.filter((_, taskIndex) => taskIndex !== index),
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');

    if (!formData.name.trim()) return setError('Service name is required.');

    const hourlyRate = formData.hourlyRate === '' ? 0 : Number(formData.hourlyRate);
    const minBookingHours = formData.minBookingHours === '' ? MIN_SERVICE_HOURS : Number(formData.minBookingHours);

    if (!Number.isFinite(hourlyRate) || hourlyRate < 0) {
      return setError('Hourly rate must be a non-negative number.');
    }
    if (!Number.isFinite(minBookingHours) || minBookingHours < MIN_SERVICE_HOURS) {
      return setError(`Minimum booking must be at least ${formatHours(MIN_SERVICE_HOURS)}.`);
    }

    const payload = {
      slug: formData.slug.trim() || slugify(formData.name),
      name: formData.name.trim(),
      icon: formData.icon,
      description: formData.description.trim(),
      status: formData.status,
      sortOrder: Number(formData.sortOrder) || 0,
      checklist: formData.checklist,
      // Backend still stores the legacy min/max fields. Keep them equal so the
      // service has one canonical hourly price everywhere without a migration.
      hourlyRateMin: hourlyRate,
      hourlyRateMax: hourlyRate,
      minBookingHours,
    };

    setSaving(true);
    try {
      if (editingService) await servicesApi.adminUpdate(editingService._id, payload);
      else await servicesApi.adminCreate(payload);
      await loadServices();
      setModalOpen(false);
      setEditingService(null);
      resetTaskDraft();
    } catch (err) {
      setError(err.message || 'Failed to save service.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await servicesApi.adminDelete(deleteConfirm);
      await loadServices();
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.message || 'Failed to delete service.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout title="Service Management">
      <div className="mb-6">
        <h2 className="m-0 font-nunito text-[24px] font-black tracking-[-0.025em] text-giggs-ink sm:text-[28px]">Services</h2>
        <p className="mb-0 mt-1 text-sm leading-6 text-giggs-muted">Manage service categories, pricing, booking minimums and client sub-service expectations.</p>
      </div>

      {error && !modalOpen && <Alert variant="danger" className="mb-4">{error}</Alert>}

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <label className="relative min-w-0 flex-1 lg:max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 z-10 -translate-y-1/2 text-giggs-muted" size={17} />
          <Input className="pl-10" placeholder="Search services…" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <Select className="lg:w-auto lg:min-w-[150px]" value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
          <option value="All">All statuses</option>
          <option value="Active">Active</option>
          <option value="Coming Soon">Coming Soon</option>
          <option value="Inactive">Inactive</option>
          <option value="Suspended">Suspended</option>
        </Select>
        <Button className="lg:ml-auto" onClick={openCreate}><Plus size={17} /> Add Service</Button>
      </div>

      <TableShell>
        <table className={`${tableClassName} min-w-[880px]`}>
          <thead>
            <tr>{['Service', 'Slug', 'Rate / Min', 'Status', 'Checklist', 'Order', 'Actions'].map((heading) => <th key={heading} className={thClassName}>{heading}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-10"><EmptyState title="Loading services…" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-10"><EmptyState title="No services found" description="Try adjusting your search or status filter." /></td></tr>
            ) : filtered.map((service) => (
              <tr key={service._id} className="hover:bg-[#F8FAF9]">
                <td className={tdClassName}>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-giggs-green/[0.08] text-giggs-green"><ServiceIcon serviceId={service.icon || service.slug} size={18} /></span>
                    <div className="min-w-0">
                      <div className="font-bold text-giggs-ink">{service.name}</div>
                      {service.description && <div className="max-w-[320px] truncate text-xs text-giggs-muted">{service.description}</div>}
                    </div>
                  </div>
                </td>
                <td className={tdClassName}><code className="rounded-lg bg-slate-100 px-2 py-1 text-xs text-giggs-text">{service.slug}</code></td>
                <td className={`${tdClassName} whitespace-nowrap text-xs`}>
                  <div className="font-bold text-giggs-ink">{serviceRate(service) > 0 ? `$${serviceRate(service)}/hr` : '—'}</div>
                  <div className="mt-0.5 text-giggs-muted">min {formatHours(Math.max(MIN_SERVICE_HOURS, Number(service.minBookingHours) || 0))}</div>
                </td>
                <td className={tdClassName}><Badge variant={STATUS_VARIANT[service.status] || 'neutral'}>{service.status}</Badge></td>
                <td className={`${tdClassName} text-xs text-giggs-text`}>{(service.checklist || []).length} sub service{(service.checklist || []).length !== 1 ? 's' : ''}</td>
                <td className={`${tdClassName} text-[13px] text-giggs-text`}>{service.sortOrder || 0}</td>
                <td className={tdClassName}>
                  <div className="flex items-center gap-1.5">
                    <Button variant="ghost" size="icon" className="!h-9 !w-9 text-giggs-green hover:bg-giggs-green/10" onClick={() => openEdit(service)} title="Edit"><Pencil size={15} /></Button>
                    <Button variant="ghost" size="icon" className="!h-9 !w-9 text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => setDeleteConfirm(service._id)} title="Delete"><Trash2 size={15} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="bg-[#FBFCFB] px-5 py-4 text-xs font-medium text-giggs-muted">Showing {filtered.length} of {services.length} services</div>
      </TableShell>

      {modalOpen && (
        <Modal title={editingService ? 'Edit Service' : 'Add New Service'} size="lg" onClose={closeModal}>
          <form onSubmit={handleSave} className="flex flex-col gap-5 px-5 pb-6 pt-3 sm:px-8 sm:pb-8">
            {error && <Alert variant="danger">{error}</Alert>}

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Service Name" required>
                <Input type="text" placeholder="e.g. Cleaning (home / apartment)" value={formData.name} onChange={(event) => { const name = event.target.value; setFormData({ ...formData, name, slug: editingService ? formData.slug : slugify(name) }); }} required />
              </Field>
              <Field label="Slug" required>
                <Input type="text" placeholder="e.g. cleaning" value={formData.slug} onChange={(event) => setFormData({ ...formData, slug: slugify(event.target.value) })} required />
              </Field>
            </div>

            <Field label="Icon">
              <div className="grid grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-2">
                {ICON_OPTIONS.map((iconId) => (
                  <Button type="button" variant="outline" size="icon" key={iconId} onClick={() => setFormData({ ...formData, icon: iconId })} className={`!h-12 !w-12 ${formData.icon === iconId ? '!bg-giggs-green/10 !text-giggs-green !ring-giggs-green' : 'text-giggs-muted'}`} title={iconId}>
                    <ServiceIcon serviceId={iconId} size={21} />
                  </Button>
                ))}
              </div>
            </Field>

            <Field label="Description">
              <Textarea rows="3" placeholder="Brief description of this service…" value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} />
            </Field>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Hourly Rate ($/hr)">
                <Input type="number" min="0" step="1" placeholder="e.g. 30" value={formData.hourlyRate} onChange={(event) => setFormData({ ...formData, hourlyRate: event.target.value })} />
              </Field>
              <Field label="Min Booking (hours)" hint="Every booked service is at least 1 hr, so two services in one booking need 2 hrs. Set more here only if this service needs it.">
                <Input type="number" min={MIN_SERVICE_HOURS} step="0.5" placeholder="e.g. 2" value={formData.minBookingHours} onChange={(event) => setFormData({ ...formData, minBookingHours: event.target.value })} />
              </Field>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Status">
                <Select value={formData.status} onChange={(event) => setFormData({ ...formData, status: event.target.value })}>
                  <option>Active</option><option>Coming Soon</option><option>Inactive</option><option>Suspended</option>
                </Select>
              </Field>
              <Field label="Sort Order"><Input type="number" value={formData.sortOrder} onChange={(event) => setFormData({ ...formData, sortOrder: event.target.value })} /></Field>
            </div>

            <Field label="Sub Services" hint="The sub services this service covers. They are labels only — a booking is priced on the hours the client books, so a sub service never adds time or cost.">
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                <Input type="text" placeholder="e.g. Change bed sheets" value={newTask} onChange={(event) => setNewTask(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTask(); } }} />
                <Button type="button" className="shrink-0" onClick={addTask}><Plus size={15} /> Add sub service</Button>
              </div>

              {formData.checklist.length === 0 ? (
                <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs text-giggs-muted">No sub services yet. Add the options a client should be able to select during booking.</div>
              ) : (
                <div className="mt-1 flex flex-col gap-2">
                  {formData.checklist.map((task, index) => (
                    <div key={`${task}-${index}`} className="grid items-center gap-2 rounded-xl bg-[#F8FAF9] px-3.5 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <span className="min-w-0 break-words text-[13px] font-medium text-[#2D3B35]">{task}</span>
                      <Button type="button" variant="ghost" size="sm" className="shrink-0 text-red-500 hover:bg-red-50 hover:text-red-600" onClick={() => removeTask(index)}>Remove</Button>
                    </div>
                  ))}
                </div>
              )}
            </Field>

            <div className="mt-1 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : (editingService ? 'Save Changes' : 'Create Service')}</Button>
            </div>
          </form>
        </Modal>
      )}

      {deleteConfirm && (
        <Modal size="sm" onClose={() => !deleting && setDeleteConfirm(null)}>
          <div className="px-5 pb-6 pt-8 text-center sm:px-8 sm:pb-8 sm:pt-10">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-600"><XCircle size={28} /></div>
            <h3 className="mb-2 mt-0 font-nunito text-xl font-black text-giggs-ink">Delete Service?</h3>
            <p className="mb-7 mt-0 text-sm leading-6 text-giggs-muted">This service will be removed permanently. This action cannot be undone.</p>
            <div className="flex flex-col-reverse justify-center gap-3 sm:flex-row">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete Service'}</Button>
            </div>
          </div>
        </Modal>
      )}
    </AdminLayout>
  );
};

export default AdminServices;